// ===========================================
// Two-Factor Authentication React Query Hooks
// Phase 2 - Priority 6
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as twoFactorApi from '../api/twoFactor.api';
import {
  TwoFactorVerifySetupRequest,
  TwoFactorDisableRequest,
  RegenerateBackupRequest,
  TwoFactorVerifyLoginRequest,
  AdminForceResetRequest,
  UpdateSecuritySettingsRequest,
} from '../api/twoFactor.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const twoFactorKeys = {
  all: ['twoFactor'] as const,
  status: () => [...twoFactorKeys.all, 'status'] as const,
  devices: () => [...twoFactorKeys.all, 'devices'] as const,
  admin: {
    all: ['twoFactor', 'admin'] as const,
    userStatus: (userId: string) => ['twoFactor', 'admin', 'user', userId] as const,
    compliance: () => ['twoFactor', 'admin', 'compliance'] as const,
    settings: () => ['twoFactor', 'admin', 'settings'] as const,
  },
};

// ===========================================
// USER 2FA QUERIES
// ===========================================

/**
 * Get current user's 2FA status
 */
export function use2FAStatus() {
  return useQuery({
    queryKey: twoFactorKeys.status(),
    queryFn: () => twoFactorApi.get2FAStatus(),
  });
}

/**
 * Get list of trusted devices
 */
export function useTrustedDevices() {
  return useQuery({
    queryKey: twoFactorKeys.devices(),
    queryFn: () => twoFactorApi.getTrustedDevices(),
  });
}

// ===========================================
// USER 2FA MUTATIONS
// ===========================================

/**
 * Initiate 2FA setup
 */
export function useInitiate2FASetup() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => twoFactorApi.initiate2FASetup(),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to initiate 2FA setup', {
        variant: 'error',
      });
    },
  });
}

/**
 * Verify 2FA setup with TOTP code
 */
export function useVerify2FASetup() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: TwoFactorVerifySetupRequest) => twoFactorApi.verify2FASetup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() });
      enqueueSnackbar('Two-factor authentication enabled successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Invalid verification code', {
        variant: 'error',
      });
    },
  });
}

/**
 * Disable 2FA
 */
export function useDisable2FA() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: TwoFactorDisableRequest) => twoFactorApi.disable2FA(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() });
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.devices() });
      enqueueSnackbar('Two-factor authentication disabled', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to disable 2FA', {
        variant: 'error',
      });
    },
  });
}

/**
 * Regenerate backup codes
 */
export function useRegenerateBackupCodes() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: RegenerateBackupRequest) => twoFactorApi.regenerateBackupCodes(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() });
      enqueueSnackbar('Backup codes regenerated successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to regenerate backup codes', {
        variant: 'error',
      });
    },
  });
}

/**
 * Verify 2FA during login
 */
export function useVerify2FALogin() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: TwoFactorVerifyLoginRequest) => twoFactorApi.verify2FALogin(data),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Invalid verification code', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// TRUSTED DEVICES MUTATIONS
// ===========================================

/**
 * Revoke a specific trusted device
 */
export function useRevokeDevice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (deviceId: string) => twoFactorApi.revokeDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.devices() });
      enqueueSnackbar('Device revoked successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to revoke device', {
        variant: 'error',
      });
    },
  });
}

/**
 * Revoke all trusted devices
 */
export function useRevokeAllDevices() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => twoFactorApi.revokeAllDevices(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.devices() });
      enqueueSnackbar(`${data.data.removedCount} device(s) revoked`, {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to revoke devices', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// ADMIN QUERIES
// ===========================================

/**
 * Get user 2FA status (admin only)
 */
export function useAdminUserStatus(userId: string) {
  return useQuery({
    queryKey: twoFactorKeys.admin.userStatus(userId),
    queryFn: () => twoFactorApi.getAdminUserStatus(userId),
    enabled: !!userId,
  });
}

/**
 * Get compliance report (admin only)
 */
export function useComplianceReport() {
  return useQuery({
    queryKey: twoFactorKeys.admin.compliance(),
    queryFn: () => twoFactorApi.getComplianceReport(),
  });
}

/**
 * Get school security settings (admin only)
 */
export function useSecuritySettings() {
  return useQuery({
    queryKey: twoFactorKeys.admin.settings(),
    queryFn: () => twoFactorApi.getSecuritySettings(),
  });
}

// ===========================================
// ADMIN MUTATIONS
// ===========================================

/**
 * Force reset user 2FA (admin only)
 */
export function useAdminForceReset2FA() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AdminForceResetRequest }) =>
      twoFactorApi.adminForceReset2FA(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.admin.userStatus(userId) });
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.admin.compliance() });
      enqueueSnackbar('User 2FA reset successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset user 2FA', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update school security settings (admin only)
 */
export function useUpdateSecuritySettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdateSecuritySettingsRequest) =>
      twoFactorApi.updateSecuritySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.admin.settings() });
      enqueueSnackbar('Security settings updated successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update security settings', {
        variant: 'error',
      });
    },
  });
}
